import { endOfMonth, startOfMonth, startOfYear, subMonths } from 'date-fns';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { FILTER_TYPES, REPORT_KEYS } from '_constants/reports';
import { theme } from '_constants/theme';
import { selectFilters } from '_store/reports/selector';
import { updateFilters } from '_store/reports/slice';

import DataTypeFilter from 'components/atomic/filters/DataTypeFilter';
import DateRangeFilter from 'components/atomic/filters/DateRangeFilter';
import MonthRangeFilter from 'components/atomic/filters/MonthRangeFilter';
import TypeFilter from 'components/atomic/filters/TypeFilter';

const defaultDateRangeFilterMap = {
  [FILTER_TYPES.dateRange]: {
    [FIELD_KEYS.dateRangeFrom]: new Date(),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
  [FILTER_TYPES.monthToDate]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(new Date()),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
  [FILTER_TYPES.lastMonth]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(subMonths(new Date(), 1)),
    [FIELD_KEYS.dateRangeTo]: endOfMonth(subMonths(new Date(), 1)),
  },
  [FILTER_TYPES.lastThreeMonth]: {
    [FIELD_KEYS.dateRangeFrom]: startOfMonth(subMonths(new Date(), 3)),
    [FIELD_KEYS.dateRangeTo]: endOfMonth(subMonths(new Date(), 1)),
  },
  [FILTER_TYPES.yearToDate]: {
    [FIELD_KEYS.dateRangeFrom]: startOfYear(new Date()),
    [FIELD_KEYS.dateRangeTo]: new Date(),
  },
};

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.largeX}px;

  > * {
    flex-grow: 1;

    &:first-child {
      width: 120px;
    }

    &:last-child {
      width: 120px;
    }
  }

  @media ${theme.devices.tablet} {
    flex-direction: column;

    > * {
      width: 100%;

      &:first-child {
        width: 100%;
      }

      &:last-child {
        width: 100%;
      }
    }
  }
`;

const Filters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters(REPORT_KEYS.insightsGraph));

  const handleFilterTypeSelect = (filter) => {
    const defaultDateRangeFilter = defaultDateRangeFilterMap[filter[FIELD_KEYS.filterType]] || {};

    dispatch(
      updateFilters({
        [REPORT_KEYS.insightsGraph]: {
          ...filters,
          ...filter,
          ...defaultDateRangeFilter,
        },
      })
    );
  };

  const handleSelect = (filter) => {
    dispatch(
      updateFilters({
        [REPORT_KEYS.insightsGraph]: {
          ...filters,
          ...filter,
        },
      })
    );
  };

  return (
    <StyledContainer>
      <TypeFilter
        name={FIELD_KEYS.filterType}
        value={filters[FIELD_KEYS.filterType]}
        onSelect={handleFilterTypeSelect}
      />
      {[FILTER_TYPES.monthToDate, FILTER_TYPES.lastMonth, FILTER_TYPES.dateRange].includes(
        filters[FIELD_KEYS.filterType]
      ) && (
        <DateRangeFilter
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={filters[FIELD_KEYS.dateRangeFrom]}
          valueTo={filters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
      {filters[FIELD_KEYS.filterType] === FILTER_TYPES.yearToDate && (
        <DateRangeFilter
          shiftByMonth
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={filters[FIELD_KEYS.dateRangeFrom]}
          valueTo={filters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
      {[FILTER_TYPES.monthRange, FILTER_TYPES.lastThreeMonth].includes(filters[FIELD_KEYS.filterType]) && (
        <MonthRangeFilter
          nameFrom={FIELD_KEYS.dateRangeFrom}
          nameTo={FIELD_KEYS.dateRangeTo}
          valueFrom={filters[FIELD_KEYS.dateRangeFrom]}
          valueTo={filters[FIELD_KEYS.dateRangeTo]}
          onSelect={handleSelect}
        />
      )}
      <DataTypeFilter name={FIELD_KEYS.type} value={filters[FIELD_KEYS.type]} onSelect={handleSelect} />
    </StyledContainer>
  );
};

export default Filters;
